import {
  PathwayUserReport,
  PlanUsersReport,
  UserPlanSummaryReport,
  UserSummaryReport,
} from '@cfa/pathway-fe-shared/xp';

import { Singular } from '@/types/utils';

import { XpUserSummaryReport } from './api-types';

import type { UserChecklistStatus } from '@/_generated/api-clients/content/types.gen';

/** @deprecated Derives from the legacy ChecklistDto-shaped helper; will be removed in PATHWAY-3174. */
export type ProcessedReportPlan = ReturnType<typeof processReportsPlan>;
/** @deprecated */
export type ProcessedReportPlanStatusItem = Singular<
  ProcessedReportPlan['status']
>;
/** @deprecated */
export type ProcessedReportPlanChecklist = Singular<
  ProcessedReportPlan['checklist']
>;

/** @deprecated Returns { checklist: { stepsTotal, ... } } legacy shape; migrate to PlanUsersReport directly. */
export const processReportsPlan = (reportPlan: PlanUsersReport) => {
  const { planUserReports, totalSteps, name, ...rest } = reportPlan;
  return {
    checklist: {
      ...rest,
      stepsTotal: totalSteps,
      // Explicitly preserve the plan name for print reports
      name,
    },
    status:
      planUserReports?.map(({ user, ...planStatus }) => ({
        ...user,
        ...planStatus,
        userId: user?.id,
        adId: user?.id,
        name: processUserName(user),
        displayName:
          user?.prefName && user.lastName
            ? `${user.prefName} ${user.lastName}`
            : user?.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : '',
      })) ?? [],
  };
};

/** @deprecated Derives from the legacy ChecklistDto-shaped helper; will be removed in PATHWAY-3174. */
export type ProcessReportsPlanResult = ReturnType<typeof processReportsPlan>;

export const processUserName = (
  user?: Partial<
    Pick<PathwayUserReport, 'firstName' | 'name' | 'lastName' | 'prefName'>
  >,
) => {
  if (user?.prefName && user.lastName) {
    return `${user.prefName} ${user.lastName}`;
  }

  if (!user || (!user.name && !user.firstName)) {
    return '';
  }
  return !user.name || user.name.includes(',')
    ? `${user.firstName} ${user.lastName}`
    : user.name;
};

/** @deprecated Returns UserChecklistStatus[]; migrate consumers to use UserPlanSummaryReport[] directly. */
// process xp getUsersSummaryReport (report team members) response to match contentApi data model
export const processReportTeamMembers = (
  users: UserPlanSummaryReport[],
  locationNumbers?: string[],
): UserChecklistStatus[] => {
  const teamMembers = users.map(({ user, plansAssigned, plansCompleted }) => ({
    plansAssigned,
    plansCompleted,
    user: {
      ...user,
      adId: user.id,
      name: processUserName(user),
      startDate:
        user.startDate && !user.startDate.includes('Z')
          ? `${user.startDate}T05:00:00Z`
          : user.startDate,
    },
  }));
  if (!locationNumbers?.length) {
    return teamMembers;
  }
  return teamMembers.filter(({ user: { locations } }) => {
    const location = locationNumbers.find(loc => locations.includes(loc));
    return !!location;
  });
};

export const processUserReportPlans = ({
  user,
  planStatus,
}: UserSummaryReport): XpUserSummaryReport => ({
  user: {
    ...user,
    adId: user.id,
    name: processUserName(user),
  },
  planStatus,
});
